import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { mdiClose } from '@mdi/js';
import { Icon } from '@mdi/react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { toast } from 'react-toastify';
import DefaultBackdrop from '../DefaultBackdrop';
import { ModificationsForm } from '../../components/ModificationsForm';
import { defineStyles } from '../../theme';
import { useShowInfoDialog } from '../../infoDialogs';
import { rowsSortingToSortDto } from '../../api/helpers';
import { useNotificationManager, notifyBatchEditResponse } from '../../notificationManager';
import { sameValuesEntries } from './helpers';
import { useHandleOk } from './useHandleOk';
const styles = defineStyles({
    root: {
        height: '100%',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
    },
    headerText: {
        display: 'flex',
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
});
export function BatchEditDialog({ open, onClose, visibleColumns, filter, sorting, totalRows, selectedRows, selectionCount, allowNewColumns, invalidateTable, batchEditApiCall, columnsToFilterInBatchEdit = [], }) {
    const [isLoading, setIsLoading] = useState(false);
    const notificationManager = useNotificationManager();
    const showInfoDialog = useShowInfoDialog();
    const formDefaultValues = useMemo(() => sameValuesEntries(selectedRows), [selectedRows]);
    const handleClose = useCallback((_, reason) => {
        if (reason === 'backdropClick') {
            return;
        }
        onClose();
    }, [onClose]);
    const columnsWithoutFiltered = useMemo(() => visibleColumns.filter((column) => column.editable && !columnsToFilterInBatchEdit.includes(column.fieldName)), [visibleColumns, columnsToFilterInBatchEdit]);
    const onOk = useHandleOk(async (modifications) => {
        setIsLoading(true);
        try {
            const ids = selectedRows.map((i) => i.ipsosId);
            const condition = {
                andConditions: filter?.andConditions ?? [],
                sorting: rowsSortingToSortDto(sorting),
                maxRecords: totalRows,
            };
            const response = await batchEditApiCall(modifications, condition, ids);
            if (!notificationManager) {
                throw new Error('BatchEditDialog must be used inside NotificationManagerProvider when batchEditApiCall returns operationId');
            }
            notifyBatchEditResponse(response, {
                trackOperation: notificationManager.trackOperation,
                invalidateTable,
                toast,
                onVerified: () => {
                    onClose();
                    setIsLoading(false);
                },
                onLegacySuccess: () => {
                    onClose();
                    setIsLoading(false);
                },
                selectedRows,
                totalRows,
                showInfoDialog,
            });
        }
        catch (error) {
            console.error(error);
            toast(_jsx(FormattedMessage, { ...error }), {
                type: 'error',
            });
            setIsLoading(false);
        }
    }, {
        selectedRows,
        totalRows,
        visibleColumns: columnsWithoutFiltered,
    });
    return (_jsxs(_Fragment, { children: [_jsxs(Dialog, { open: open, maxWidth: "sm", fullWidth: true, onClose: handleClose, sx: styles.root, children: [_jsxs(DialogTitle, { sx: styles.header, children: [_jsx(Box, { sx: styles.headerText, children: _jsx(FormattedMessage, { id: "batchEditDialog.header_edit_n", defaultMessage: "Batch Edit | Editing {n} rows", values: { n: selectionCount === 0 ? totalRows : selectionCount } }) }), _jsx(ButtonBase, { onClick: onClose, disableRipple: true, children: _jsx(Icon, { path: mdiClose, size: 1 }) })] }), _jsx(DialogContent, { sx: styles.content, children: _jsx(ModificationsForm, { onSubmit: onOk, onCancel: onClose, visibleColumns: columnsWithoutFiltered, defaultValues: formDefaultValues, allowNewColumns: allowNewColumns }) })] }), _jsx(DefaultBackdrop, { isLoading: isLoading })] }));
}
//# sourceMappingURL=BatchEditDialog.js.map